import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { examsAPI, studentsAPI, attendanceAPI, examScheduleAPI } from '../services/api';
import { useYear } from '../context/YearContext';
import HomeworkAssignments from './HomeworkAssignments';
import TimetableEvents from './TimetableEvents';

// ── Grade calculator (mirrors backend) ──────────────────────────────────────
function calcGrade(obtained, max) {
  if (!max || max <= 0) return '';
  const pct = (parseFloat(obtained) / parseInt(max, 10)) * 100;
  if (pct >= 90) return 'A+';
  if (pct >= 80) return 'A';
  if (pct >= 70) return 'B+';
  if (pct >= 60) return 'B';
  if (pct >= 50) return 'C';
  if (pct >= 35) return 'D';
  return 'F';
}

const GRADE_COLORS = { 'A+': '#16a34a', A: '#22c55e', 'B+': '#84cc16', B: '#eab308', C: '#f97316', D: '#ef4444', F: '#dc2626', '': '' };

// ── Excel helpers ─────────────────────────────────────────────────────────────
function downloadTemplate(examName, className, subjects, students) {
  // Header row: Student Name | Subject1 | Subject2 | ...
  const header = ['Student Name', ...subjects.map((s) => `${s.subject_name}(${s.max_marks})`)];
  const rows   = students.map((st) => [st.student_name, ...subjects.map(() => '')]);
  const ws     = XLSX.utils.aoa_to_sheet([header, ...rows]);

  // Column widths
  ws['!cols'] = [{ wch: 24 }, ...subjects.map(() => ({ wch: 14 }))];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Marks');
  XLSX.writeFile(wb, `${examName}_${className}_template.xlsx`);
}

function parseExcelUpload(file, subjects, students) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (rows.length < 2) return reject(new Error('Excel file is empty or has no data rows.'));

        const headerRow = rows[0].map((h) => String(h).trim());

        // Map header → subject index
        // Header format: "Math(50)" or just "Math"
        const subjectColMap = {};
        subjects.forEach((s) => {
          const fullHeader = `${s.subject_name}(${s.max_marks})`;
          let colIdx = headerRow.indexOf(fullHeader);
          if (colIdx === -1) colIdx = headerRow.findIndex((h) => h.toLowerCase() === s.subject_name.toLowerCase());
          if (colIdx !== -1) subjectColMap[s.subject_name] = colIdx;
        });

        if (!Object.keys(subjectColMap).length)
          return reject(new Error('No matching subject columns found. Please use the downloaded template.'));

        // Build a name → student map (case-insensitive)
        const studentMap = {};
        students.forEach((st) => { studentMap[st.student_name.toLowerCase().trim()] = st; });

        const parsed   = {}; // { studentId: { subjectName: value } }
        const warnings = [];

        rows.slice(1).forEach((row, i) => {
          const name = String(row[0] || '').trim();
          if (!name) return;
          const st = studentMap[name.toLowerCase()];
          if (!st) { warnings.push(`Row ${i + 2}: "${name}" not found in student list — skipped.`); return; }

          parsed[st.student_id] = {};
          subjects.forEach((s) => {
            const colIdx = subjectColMap[s.subject_name];
            if (colIdx === undefined) return;
            const raw = String(row[colIdx] ?? '').trim();
            const val  = raw === '' ? '' : String(parseFloat(raw) || 0);
            parsed[st.student_id][s.subject_name] = val;
          });
        });

        resolve({ parsed, warnings });
      } catch (err) {
        reject(new Error('Could not read Excel file: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsArrayBuffer(file);
  });
}

// ── PDF result card generator ────────────────────────────────────────────────
function buildResultPDF(studentName, fatherName, examName, examType, className, examDate, subjects, resultsForStudent, schoolName, schoolLocation) {
  const doc  = new jsPDF({ unit: 'mm', format: 'a5' });
  const W    = doc.internal.pageSize.getWidth();
  const PAD  = 12;

  // ── Header band ─────────────────────────────────────────────────────
  doc.setFillColor(30, 58, 138);   // dark blue
  doc.rect(0, 0, W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(schoolName || 'School', W / 2, 10, { align: 'center' });
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`${schoolLocation ? schoolLocation + ' · ' : ''}Exam Result Report`, W / 2, 17, { align: 'center' });

  // ── Exam info band ───────────────────────────────────────────────────
  doc.setFillColor(239, 246, 255);
  doc.rect(0, 22, W, 18, 'F');
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(examName, PAD, 30);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  const examTypeLabel = { weekly: 'Weekly Test', monthly: 'Monthly Test', annual: 'Annual Exam' };
  const dateStr = examDate ? new Date(examDate).toLocaleDateString('en-IN') : '';
  doc.text(`${examTypeLabel[examType] || examType} · Class ${className}${dateStr ? ' · ' + dateStr : ''}`, PAD, 36);

  // ── Student info ──────────────────────────────────────────────────────
  let y = 48;
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(PAD, y - 5, W - PAD * 2, 16, 2, 2, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(studentName, PAD + 3, y + 2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(`Father: ${fatherName || '—'}`, PAD + 3, y + 8);
  y += 20;

  // ── Subjects table ────────────────────────────────────────────────────
  const subjectRows = subjects.map((s) => {
    const r = resultsForStudent[s.subject_name];
    if (!r) return null;
    const obtained = parseFloat(r.marks_obtained);
    const pass     = obtained >= s.max_marks * 0.35;
    return { name: s.subject_name, obtained, max: s.max_marks, grade: r.grade, pass };
  }).filter(Boolean);

  if (!subjectRows.length) { doc.save(`${studentName}_result.pdf`); return; }

  const totalObt     = subjectRows.reduce((s, r) => s + r.obtained, 0);
  const totalMax     = subjectRows.reduce((s, r) => s + r.max, 0);
  const overallGrade = calcGrade(totalObt, totalMax);
  const failedSubs   = subjectRows.filter((r) => !r.pass).map((r) => r.name);
  const passed       = failedSubs.length === 0;

  // Table header
  const colX   = [PAD, PAD + 55, PAD + 80, PAD + 105];
  const rowH   = 8;
  doc.setFillColor(30, 58, 138);
  doc.rect(PAD, y, W - PAD * 2, rowH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Subject',    colX[0] + 2, y + 5.5);
  doc.text('Marks',      colX[1] + 2, y + 5.5);
  doc.text('Max',        colX[2] + 2, y + 5.5);
  doc.text('Grade',      colX[3] + 2, y + 5.5);
  y += rowH;

  const gradeColor = { 'A+': [22,163,74], A: [34,197,94], 'B+': [132,204,22], B: [234,179,8], C: [249,115,22], D: [239,68,68], F: [220,38,38] };

  subjectRows.forEach((r, i) => {
    doc.setFillColor(i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 250 : 255, i % 2 === 0 ? 252 : 255);
    doc.rect(PAD, y, W - PAD * 2, rowH, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', r.pass ? 'normal' : 'bold');
    doc.text(r.name,              colX[0] + 2, y + 5.5);
    doc.setFont('helvetica', 'normal');
    doc.text(String(r.obtained),  colX[1] + 2, y + 5.5);
    doc.text(String(r.max),       colX[2] + 2, y + 5.5);
    const gc = gradeColor[r.grade] || [15, 23, 42];
    doc.setTextColor(...gc);
    doc.setFont('helvetica', 'bold');
    doc.text(r.grade === 'F' ? 'FAIL' : r.grade, colX[3] + 2, y + 5.5);
    y += rowH;
  });

  // Total row
  doc.setFillColor(219, 234, 254);
  doc.rect(PAD, y, W - PAD * 2, rowH, 'F');
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL', colX[0] + 2, y + 5.5);
  doc.text(String(totalObt), colX[1] + 2, y + 5.5);
  doc.text(String(totalMax), colX[2] + 2, y + 5.5);
  const gc = gradeColor[overallGrade] || [30, 58, 138];
  doc.setTextColor(...gc);
  doc.text(overallGrade, colX[3] + 2, y + 5.5);
  y += rowH + 6;

  // ── Result banner ─────────────────────────────────────────────────────
  const passColor = passed ? [220, 252, 231] : [254, 226, 226];
  const passText  = passed ? [22, 101, 52]   : [153, 27, 27];
  doc.setFillColor(...passColor);
  doc.roundedRect(PAD, y, W - PAD * 2, 12, 2, 2, 'F');
  doc.setTextColor(...passText);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const resultLabel = passed ? '✓  PASS' : `✗  Compartment: ${failedSubs.join(', ')}`;
  doc.text(resultLabel, W / 2, y + 8, { align: 'center' });
  y += 18;

  // ── Footer ────────────────────────────────────────────────────────────
  doc.setDrawColor(203, 213, 225);
  doc.line(PAD, y, W - PAD, y);
  doc.setTextColor(148, 163, 184);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('This is a computer-generated report.', W / 2, y + 5, { align: 'center' });
  doc.text(`${schoolName || 'School'}${schoolLocation ? ' · ' + schoolLocation : ''}`, W / 2, y + 10, { align: 'center' });

  doc.save(`${studentName}_${examName}_result.pdf`);
}

// ── WhatsApp message builder (English only) ─────────────────────────────────
function buildWaMessage(studentName, fatherName, examName, examType, className, examDate, subjects, resultsForStudent, schoolName, schoolLocation) {
  const subjectRows = subjects.map((s) => {
    const r = resultsForStudent[s.subject_name];
    if (!r) return null;
    const obtained = parseFloat(r.marks_obtained);
    const pass     = obtained >= (s.max_marks * 0.35);
    return { name: s.subject_name, obtained, max: s.max_marks, grade: r.grade, pass };
  }).filter(Boolean);

  if (!subjectRows.length) return '';

  const totalObt       = subjectRows.reduce((s, r) => s + r.obtained, 0);
  const totalMax       = subjectRows.reduce((s, r) => s + r.max, 0);
  const overallGrade   = calcGrade(totalObt, totalMax);
  const failedSubjects = subjectRows.filter((r) => !r.pass).map((r) => r.name);
  const passed         = failedSubjects.length === 0;
  const dateStr        = examDate ? new Date(examDate).toLocaleDateString('en-IN') : '';

  const rows = subjectRows.map((r) =>
    `  ${r.name}: ${r.obtained}/${r.max} — ${r.grade === 'F' ? '❌ Fail' : `✅ ${r.grade}`}`
  ).join('\n');

  return (
    `🏫 *${schoolName || 'School'}${schoolLocation ? ', ' + schoolLocation : ''}*\n` +
    `━━━━━━━━━━━━━━━━━━━━━━\n` +
    `📋 *Exam:* ${examName}\n` +
    `🏷️ *Class:* ${className}${dateStr ? `  📅 ${dateStr}` : ''}\n` +
    `━━━━━━━━━━━━━━━━━━━━━━\n` +
    `👦 *Student:* ${studentName}\n` +
    `👨 *Father:* ${fatherName || '—'}\n` +
    `━━━━━━━━━━━━━━━━━━━━━━\n` +
    `📚 *Subjects & Marks:*\n` +
    `${rows}\n` +
    `━━━━━━━━━━━━━━━━━━━━━━\n` +
    `📊 *Total:* ${totalObt}/${totalMax}  |  *Grade: ${overallGrade}*\n` +
    `🏆 *Result:* ${passed ? '✅ Pass' : `❌ Compartment in ${failedSubjects.join(', ')}`}\n` +
    `━━━━━━━━━━━━━━━━━━━━━━\n` +
    `Thank you 🙏\n${schoolName || 'School'}`
  );
}

// ── Exam Setup Form ──────────────────────────────────────────────────────────
function ExamSetupForm({ classes, year, onSaved, onCancel }) {
  const [form, setForm] = useState({
    exam_name: '', exam_type: 'weekly', class: '', exam_date: '',
  });
  const [subjects, setSubjects] = useState([{ subject_name: '', max_marks: '100' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const addSubject    = () => setSubjects((p) => [...p, { subject_name: '', max_marks: '100' }]);
  const removeSubject = (i) => setSubjects((p) => p.filter((_, idx) => idx !== i));
  const updateSubject = (i, field, val) =>
    setSubjects((p) => p.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));

  const handleSave = async () => {
    setError('');
    if (!form.exam_name.trim()) return setError('Exam name is required');
    if (!form.class)            return setError('Class is required');
    const validSubs = subjects.filter((s) => s.subject_name.trim());
    if (!validSubs.length)      return setError('At least one subject is required');

    setSaving(true);
    try {
      const type = form.exam_type?.toLowerCase() || '';

const normalizedType =
  type.includes('week') ? 'weekly' :
  type.includes('month') ? 'monthly' :
  type.includes('annual') ? 'annual' :
  form.exam_type;
    await examsAPI.create({
  ...form,
  exam_type: normalizedType,
  academic_year: year,
  subjects: validSubs
});
onSaved();

} catch (err) {
  setError(err.response?.data?.message || err.message);

} finally {
  setSaving(false);
}
  };

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="modal-box" style={{ maxWidth: 580, padding: 0, borderRadius: 10, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 18 }}>📝 Create New Exam</h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* Error banner */}
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
              borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontWeight: 500, fontSize: 14, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span>⚠️</span><span>{error}</span>
            </div>
          )}

          {/* Exam Name */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Exam Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input value={form.exam_name} onChange={(e) => setForm({ ...form, exam_name: e.target.value })}
              placeholder="e.g. Unit Test 1, Weekly Test 3"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
          </div>

          {/* Type / Class / Date row */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Exam Type <span style={{ color: '#ef4444' }}>*</span></label>
          <input
  type="text"
  value={form.exam_type}
  onChange={(e) => setForm({ ...form, exam_type: e.target.value })}
  placeholder="e.g. weekly, monthly"
  style={{
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14
  }}
/>
          
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Class <span style={{ color: '#ef4444' }}>*</span></label>
              <select value={form.class} onChange={(e) => setForm({ ...form, class: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}>
                <option value="">-- Select --</option>
                {classes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Exam Date</label>
              <input type="date" value={form.exam_date} onChange={(e) => setForm({ ...form, exam_date: e.target.value })} lang="en-GB"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
            </div>
          </div>

          {/* Subjects section */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>📚 Subjects &amp; Max Marks</label>
              <button className="btn btn-sm btn-outline" onClick={addSubject}
                style={{ fontSize: 12, padding: '4px 10px' }}>+ Add Subject</button>
            </div>
            {subjects.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                <span style={{ width: 22, textAlign: 'right', color: '#9ca3af', fontSize: 12, flexShrink: 0 }}>{i + 1}.</span>
                <input placeholder="Subject name (e.g. Math, Telugu)" value={s.subject_name}
                  onChange={(e) => updateSubject(i, 'subject_name', e.target.value)}
                  style={{ flex: 3, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                <input type="number" placeholder="Max" value={s.max_marks} min="1" max="999"
                  onChange={(e) => updateSubject(i, 'max_marks', e.target.value)}
                  style={{ flex: 1, padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, textAlign: 'center' }} />
                {subjects.length > 1 ? (
                  <button onClick={() => removeSubject(i)}
                    style={{ background: '#fee2e2', border: 'none', color: '#ef4444', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>✕</button>
                ) : (
                  <span style={{ width: 28, flexShrink: 0 }} />
                )}
              </div>
            ))}
          </div>

          {/* Footer actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button onClick={onCancel} disabled={saving}
              style={{ padding: '9px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '9px 22px', border: 'none', borderRadius: 6, background: saving ? '#a5b4fc' : '#4f46e5', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14 }}>
              {saving ? '⏳ Saving...' : '✅ Create Exam'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Exam Analytics Modal ──────────────────────────────────────────────────────
function ExamAnalyticsModal({ examId, examName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    examsAPI.getResults(examId)
      .then(res => {
        const { subjects, students, savedResults } = res.data.data;
        if (!students.length || !subjects.length) { setAnalytics(null); setLoading(false); return; }

        const subjectStats = subjects.map(sub => {
          const marks = students
            .map(st => savedResults?.[st.student_id]?.[sub.subject_name])
            .filter(r => r !== undefined && r !== null)
            .map(r => parseFloat(r.marks_obtained));

          if (!marks.length) return { name: sub.subject_name, max: sub.max_marks, count: 0, avg: null, pass: null, fail: null };
          const avg  = marks.reduce((a, b) => a + b, 0) / marks.length;
          const pass = marks.filter(m => m >= sub.max_marks * 0.35).length;
          return { name: sub.subject_name, max: sub.max_marks, count: marks.length, avg: avg.toFixed(1), pass, fail: marks.length - pass };
        });

        // Grade distribution across all students (overall grade)
        const gradeDist = { 'A+': 0, A: 0, 'B+': 0, B: 0, C: 0, D: 0, F: 0 };
        let graded = 0;
        students.forEach(st => {
          const saved = savedResults?.[st.student_id];
          if (!saved || !Object.keys(saved).length) return;
          let totalObt = 0, totalMax = 0;
          subjects.forEach(sub => {
            const r = saved[sub.subject_name];
            if (r) { totalObt += parseFloat(r.marks_obtained); totalMax += sub.max_marks; }
          });
          if (totalMax > 0) { gradeDist[calcGrade(totalObt, totalMax)]++; graded++; }
        });

        setAnalytics({ subjectStats, gradeDist, graded, total: students.length });
        setLoading(false);
      })
      .catch(err => { setError(err.response?.data?.message || err.message); setLoading(false); });
  }, [examId]);

  const gradeColors = { 'A+': '#16a34a', A: '#22c55e', 'B+': '#84cc16', B: '#eab308', C: '#f97316', D: '#ef4444', F: '#dc2626' };

  return createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 640, padding: 0, borderRadius: 10, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 18 }}>📊 Analytics — {examName}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
          {loading ? <div className="loading">Loading...</div> : error ? (
            <div className="alert alert-error">{error}</div>
          ) : !analytics ? (
            <div className="empty-state"><p>No results entered for this exam yet.</p></div>
          ) : (
            <>
              {/* Subject-wise stats */}
              <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#374151' }}>Subject-wise Performance</h4>
              <div className="table-container" style={{ marginBottom: 20 }}>
                <table className="data-table" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th style={{ textAlign: 'center' }}>Max</th>
                      <th style={{ textAlign: 'center' }}>Avg Marks</th>
                      <th style={{ textAlign: 'center' }}>Avg %</th>
                      <th style={{ textAlign: 'center' }}>Pass</th>
                      <th style={{ textAlign: 'center' }}>Fail</th>
                      <th style={{ textAlign: 'center' }}>Pass Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.subjectStats.map(s => {
                      const avgPct = s.avg !== null && s.max > 0 ? ((parseFloat(s.avg) / s.max) * 100).toFixed(1) : '—';
                      const passRate = s.count > 0 ? ((s.pass / s.count) * 100).toFixed(0) : '—';
                      return (
                        <tr key={s.name}>
                          <td style={{ fontWeight: 500 }}>{s.name}</td>
                          <td style={{ textAlign: 'center' }}>{s.max}</td>
                          <td style={{ textAlign: 'center', fontWeight: 600 }}>{s.avg ?? '—'}</td>
                          <td style={{ textAlign: 'center' }}>{avgPct !== '—' ? `${avgPct}%` : '—'}</td>
                          <td style={{ textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>{s.pass ?? '—'}</td>
                          <td style={{ textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>{s.fail ?? '—'}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: passRate !== '—' && parseInt(passRate) >= 60 ? '#16a34a' : '#ef4444' }}>
                            {passRate !== '—' ? `${passRate}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Overall grade distribution */}
              <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#374151' }}>Overall Grade Distribution ({analytics.graded} / {analytics.total} students graded)</h4>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {Object.entries(analytics.gradeDist).map(([grade, count]) => (
                  <div key={grade} style={{
                    textAlign: 'center', minWidth: 64, padding: '10px 14px', borderRadius: 8,
                    background: count > 0 ? '#f8fafc' : '#f1f5f9',
                    border: `2px solid ${count > 0 ? gradeColors[grade] : '#e2e8f0'}`
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: count > 0 ? gradeColors[grade] : '#cbd5e1' }}>{count}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: count > 0 ? gradeColors[grade] : '#94a3b8' }}>{grade}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Exam Edit Form ────────────────────────────────────────────────────────────
function ExamEditForm({ exam, onSaved, onCancel }) {
  const [form, setForm] = useState({
    exam_name: exam.exam_name,
    exam_type: exam.exam_type,
    exam_date: exam.exam_date || '',
  });
  const [subjects, setSubjects] = useState(
    exam.subjects && exam.subjects.length
      ? exam.subjects.map(s => ({ subject_name: s.subject_name, max_marks: String(s.max_marks) }))
      : [{ subject_name: '', max_marks: '100' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const addSubject    = () => setSubjects(p => [...p, { subject_name: '', max_marks: '100' }]);
  const removeSubject = (i) => setSubjects(p => p.filter((_, idx) => idx !== i));
  const updateSubject = (i, field, val) =>
    setSubjects(p => p.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));

  const handleSave = async () => {
    setError('');
    if (!form.exam_name.trim()) return setError('Exam name is required');
    const validSubs = subjects.filter(s => s.subject_name.trim());
    if (!validSubs.length) return setError('At least one subject is required');
    setSaving(true);
    try {
      await examsAPI.update(exam.exam_id, {
        exam_name: form.exam_name.trim(),
        exam_type: form.exam_type.trim(),
        exam_date: form.exam_date || null,
        subjects:  validSubs,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal-box" style={{ maxWidth: 580, padding: 0, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ background: 'linear-gradient(135deg, #0369a1, #0284c7)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: 18 }}>✏️ Edit Exam</h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
              borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontWeight: 500, fontSize: 14, display: 'flex', gap: 8 }}>
              <span>⚠️</span><span>{error}</span>
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Exam Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input value={form.exam_name} onChange={e => setForm({ ...form, exam_name: e.target.value })}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Exam Type <span style={{ color: '#ef4444' }}>*</span></label>
              <select value={form.exam_type} onChange={e => setForm({ ...form, exam_type: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}>
                <option value="weekly">Weekly Test</option>
                <option value="monthly">Monthly Test</option>
                <option value="annual">Annual Exam</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Exam Date</label>
              <input type="date" value={form.exam_date} onChange={e => setForm({ ...form, exam_date: e.target.value })} lang="en-GB"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
            </div>
          </div>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>📚 Subjects &amp; Max Marks</label>
              <button className="btn btn-sm btn-outline" onClick={addSubject} style={{ fontSize: 12, padding: '4px 10px' }}>+ Add Subject</button>
            </div>
            {subjects.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                <span style={{ width: 22, textAlign: 'right', color: '#9ca3af', fontSize: 12, flexShrink: 0 }}>{i + 1}.</span>
                <input placeholder="Subject name" value={s.subject_name}
                  onChange={e => updateSubject(i, 'subject_name', e.target.value)}
                  style={{ flex: 3, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                <input type="number" placeholder="Max" value={s.max_marks} min="1" max="999"
                  onChange={e => updateSubject(i, 'max_marks', e.target.value)}
                  style={{ flex: 1, padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, textAlign: 'center' }} />
                {subjects.length > 1 ? (
                  <button onClick={() => removeSubject(i)}
                    style={{ background: '#fee2e2', border: 'none', color: '#ef4444', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>✕</button>
                ) : (
                  <span style={{ width: 28, flexShrink: 0 }} />
                )}
              </div>
            ))}
          </div>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginTop: 12, fontSize: 12, color: '#92400e' }}>
            ⚠️ Existing student results are kept. Only exam name, date, type and subject list are updated.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button onClick={onCancel} disabled={saving}
              style={{ padding: '9px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '9px 22px', border: 'none', borderRadius: 6, background: saving ? '#7dd3fc' : '#0284c7', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14 }}>
              {saving ? '⏳ Saving...' : '✅ Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Mark Entry Screen ────────────────────────────────────────────────────────
function MarkEntry({ examId, onBack }) {
  const schoolName     = sessionStorage.getItem('gs_school_name')     || 'School';
  const schoolLocation = sessionStorage.getItem('gs_school_location') || '';
  const [data,        setData]        = useState(null);
  const [marks,       setMarks]       = useState({});
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState('');
  const [loading,     setLoading]     = useState(true);
  const [xlsxWarnings, setXlsxWarnings] = useState([]);
  const [xlsxInfo,    setXlsxInfo]    = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    examsAPI.getResults(examId)
      .then((res) => {
        const d = res.data.data;
        setData(d);
        // Pre-fill saved results
        const init = {};
        for (const st of d.students) {
          init[st.student_id] = {};
          for (const sub of d.subjects) {
            const saved = d.savedResults?.[st.student_id]?.[sub.subject_name];
            init[st.student_id][sub.subject_name] = saved ? String(saved.marks_obtained) : '';
          }
        }
        setMarks(init);
      })
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [examId]);

  const setMark = (studentId, subjectName, val) => {
    setMarks((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [subjectName]: val },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    const results = [];
    for (const st of data.students) {
      for (const sub of data.subjects) {
        const val = marks[st.student_id]?.[sub.subject_name];
        if (val === '' || val === undefined) continue;
        results.push({
          student_id:     st.student_id,
          subject_name:   sub.subject_name,
          marks_obtained: parseFloat(val) || 0,
          max_marks:      sub.max_marks,
        });
      }
    }
    try {
      await examsAPI.saveResults(examId, results);
      setSaved(true);
      // Refresh grades display
      const res = await examsAPI.getResults(examId);
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const waLink = (student) => {
    if (!student.mobile_number) return null;
    const subjectResults = data.savedResults?.[student.student_id] || {};
    const msg = buildWaMessage(
      student.student_name,
      student.father_name,
      data.exam.exam_name,
      data.exam.exam_type,
      data.exam.class,
      data.exam.exam_date,
      data.subjects,
      subjectResults,
      schoolName,
      schoolLocation
    );
    return `https://wa.me/91${student.mobile_number}?text=${encodeURIComponent(msg)}`;
  };

  const handleDownloadTemplate = () => {
    downloadTemplate(data.exam.exam_name, data.exam.class, data.subjects, data.students);
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setXlsxWarnings([]);
    setXlsxInfo('');
    try {
      const { parsed, warnings } = await parseExcelUpload(file, data.subjects, data.students);
      // Merge parsed marks into current marks state
      setMarks((prev) => {
        const next = { ...prev };
        for (const [studentId, subjectMarks] of Object.entries(parsed)) {
          next[studentId] = { ...(next[studentId] || {}), ...subjectMarks };
        }
        return next;
      });
      const matched  = Object.keys(parsed).length;
      const skipped  = warnings.length;
      const totalMarks = Object.values(parsed).reduce((sum, subMap) =>
        sum + Object.values(subMap).filter(v => v !== '').length, 0);
      setXlsxInfo(`✅ ${matched} student${matched !== 1 ? 's' : ''} matched, ${totalMarks} marks loaded${skipped ? `, ${skipped} row${skipped !== 1 ? 's' : ''} skipped` : ''}. Review below then click Save.`);
      setXlsxWarnings(warnings);
      setSaved(false);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!data)   return <div className="alert alert-error">{error}</div>;

  const { exam, subjects, students, savedResults } = data;
  const examTypeLabel = { weekly: 'Weekly Test', monthly: 'Monthly Test', annual: 'Annual Exam' };
  const sc = sessionStorage.getItem('gs_school_code');
  const progressCardUrl = (studentId) =>
    `${window.location.origin}/progress-card/${encodeURIComponent(sc)}/${studentId}`;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <div>
          <h3 style={{ margin: 0 }}>{exam.exam_name}</h3>
          <span style={{ color: '#888', fontSize: 13 }}>
            {examTypeLabel[exam.exam_type] || exam.exam_type} · Class {exam.class} · {exam.academic_year}
            {exam.exam_date ? ` · ${new Date(exam.exam_date).toLocaleDateString('en-IN')}` : ''}
          </span>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
      {saved && <div className="alert alert-success" style={{ marginBottom: 12 }}>✅ Results saved successfully!</div>}

      {/* Excel toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap',
        background: '#eff6ff', padding: '8px 14px', borderRadius: 8, border: '1px solid #bfdbfe' }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>📊 Excel:</span>
        <button className="btn btn-sm btn-outline" onClick={handleDownloadTemplate}>
          ⬇️ Download Template
        </button>
        <button className="btn btn-sm btn-outline" onClick={() => fileInputRef.current?.click()}>
          ⬆️ Upload Filled Excel
        </button>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
          onChange={handleUploadExcel} />
        <span style={{ color: '#64748b', fontSize: 12 }}>
          Download → fill marks → upload → save
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <button
            className="btn btn-sm"
            style={{ background: '#1a237e', color: '#fff', border: 'none', fontWeight: 600 }}
            onClick={() => {
              const ids = students.map(st => st.student_id).join(',');
              window.open(
                `${window.location.origin}/progress-card/${encodeURIComponent(sc)}/print-all?ids=${ids}`,
                '_blank'
              );
            }}
            title="Open all progress cards in a single printable page"
          >
            📊 Print All Progress Cards
          </button>
        </div>
      </div>

      {/* Excel feedback */}
      {xlsxInfo && (
        <div className="alert alert-success" style={{ marginBottom: 8, fontSize: 13 }}>{xlsxInfo}</div>
      )}
      {xlsxWarnings.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
          padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
          <strong>⚠️ Warnings:</strong>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {xlsxWarnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {students.length === 0 ? (
        <div className="alert alert-info">No active students found for Class {exam.class} in {exam.academic_year}.</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student Name</th>
                  <th>Father</th>
                  {subjects.map((s) => (
                    <th key={s.subject_id}>{s.subject_name}<br /><small style={{ fontWeight: 400 }}>/{s.max_marks}</small></th>
                  ))}
                  <th>Grade</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map((st, idx) => {
                  // Compute overall grade from saved results only
                  const saved = savedResults?.[st.student_id] || {};
                  const hasSaved = Object.keys(saved).length > 0;
                  let totalObt = 0, totalMax = 0;
                  subjects.forEach((s) => {
                    const r = saved[s.subject_name];
                    if (r) { totalObt += parseFloat(r.marks_obtained); totalMax += s.max_marks; }
                  });
                  const overallGrade = hasSaved && totalMax > 0 ? calcGrade(totalObt, totalMax) : '';
                  const link = waLink(st);

                  return (
                    <tr key={st.student_id}>
                      <td>{idx + 1}</td>
                      <td style={{ fontWeight: 500 }}>{st.student_name}</td>
                      <td style={{ color: '#888', fontSize: 13 }}>{st.father_name}</td>
                      {subjects.map((s) => {
                        const val = marks[st.student_id]?.[s.subject_name] ?? '';
                        const grade = val !== '' ? calcGrade(val, s.max_marks) : '';
                        return (
                          <td key={s.subject_id} style={{ padding: '4px 6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="number" min="0" max={s.max_marks} step="0.5"
                                value={val}
                                onChange={(e) => setMark(st.student_id, s.subject_name, e.target.value)}
                                style={{ width: 60, textAlign: 'center' }}
                              />
                              {grade && (
                                <span style={{ fontSize: 11, fontWeight: 700, color: GRADE_COLORS[grade] || '#333' }}>
                                  {grade}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'center', fontWeight: 700, color: GRADE_COLORS[overallGrade] || '#888' }}>
                        {overallGrade || '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {hasSaved ? (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <a href={progressCardUrl(st.student_id)} target="_blank" rel="noreferrer"
                              title="View Progress Card"
                              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5,
                                background: '#1a237e', color: '#fff', textDecoration: 'none', fontWeight: 600 }}>
                              📊
                            </a>
                            {link && (
                              <a href={link} target="_blank" rel="noreferrer"
                                style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5,
                                  background: '#25d366', color: '#fff', textDecoration: 'none', fontWeight: 600 }}>
                                📱
                              </a>
                            )}
                            <button
                              onClick={() => buildResultPDF(
                                st.student_name, st.father_name,
                                exam.exam_name, exam.exam_type, exam.class, exam.exam_date,
                                subjects, savedResults?.[st.student_id] || {},
                                schoolName, schoolLocation
                              )}
                              style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5,
                                background: '#3b82f6', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                              📄 PDF
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: '#ccc', fontSize: 12 }}>Save first</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Results'}
            </button>
            <button
              className="btn btn-sm"
              style={{ background: '#fff5f5', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, padding: '7px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
              onClick={async () => {
                const hasSaved = students.some(st => Object.keys(data.savedResults?.[st.student_id] || {}).length > 0);
                if (!hasSaved) return alert('No saved results to clear.');
                if (!window.confirm('Clear ALL saved results for this exam? This cannot be undone. The exam and subjects are kept.')) return;
                try {
                  await examsAPI.clearResults(examId);
                  const res = await examsAPI.getResults(examId);
                  const d = res.data.data;
                  setData(d);
                  const init = {};
                  for (const st of d.students) {
                    init[st.student_id] = {};
                    for (const sub of d.subjects) init[st.student_id][sub.subject_name] = '';
                  }
                  setMarks(init);
                  setSaved(false);
                } catch (err) {
                  alert('Failed to clear results: ' + (err.response?.data?.message || err.message));
                }
              }}
            >
              🗑️ Clear All Results
            </button>
            <span style={{ color: '#888', fontSize: 13 }}>
              {students.length} students · {subjects.length} subjects
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Exam Schedule Panel ───────────────────────────────────────────────────────
function ExamSchedulePanel() {
  const { year } = useYear();

  const [examName,  setExamName]  = useState('');
  const [className, setClassName] = useState('All Classes');
  const [rows,      setRows]      = useState([]);   // saved rows from server
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  // draft row form
  const [draft, setDraft] = useState({ exam_date: '', subject: '', syllabus: '' });

  const load = useCallback(() => {
    if (!year || !examName.trim()) return;
    setLoading(true);
    setError('');
    examScheduleAPI.getAll({ year, exam_name: examName.trim(), class: className })
      .then((res) => setRows(res.data.data || []))
      .catch(() => setError('Failed to load schedule'))
      .finally(() => setLoading(false));
  }, [year, examName, className]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!draft.exam_date || !draft.subject.trim()) return;
    setSaving(true);
    setError('');
    try {
      await examScheduleAPI.create({
        academic_year: year,
        exam_name:     examName.trim(),
        class:         className,
        exam_date:     draft.exam_date,
        subject:       draft.subject.trim().toUpperCase(),
        syllabus:      draft.syllabus.trim(),
      });
      setDraft({ exam_date: '', subject: '', syllabus: '' });
      load();
    } catch {
      setError('Failed to add row');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this row?')) return;
    try {
      await examScheduleAPI.delete(id);
      load();
    } catch {
      setError('Failed to delete row. Please try again.');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>📅 Exam Schedule</h2>
        <p style={{ margin: '6px 0 0', color: '#64748b' }}>
          Manage exam timetables with date, subject and syllabus.
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <input
          style={{ padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, minWidth: 200 }}
          placeholder="Exam Name (e.g. Half-Yearly)"
          value={examName}
          onChange={(e) => setExamName(e.target.value)}
        />
        <input
          style={{ padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, minWidth: 140 }}
          placeholder="Class (e.g. 10A)"
          value={className}
          onChange={(e) => setClassName(e.target.value)}
        />
        <button className="btn btn-secondary" onClick={load} disabled={!examName.trim()}>
          🔍 Load
        </button>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

      {/* Timetable table */}
      <div style={{ overflowX: 'auto', marginBottom: 18 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e3a5f', color: '#fff' }}>
              <th style={thStyle}>📅 DATE</th>
              <th style={thStyle}>SUBJECT</th>
              <th style={thStyle}>SYLLABUS / TOPIC</th>
              <th style={{ ...thStyle, width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>
                {examName.trim() ? 'No rows yet. Add rows below.' : 'Enter an exam name and click Load.'}
              </td></tr>
            )}
            {rows.map((row, i) => (
              <tr key={row.schedule_id} style={{ background: i % 2 === 0 ? '#f9fafb' : '#fff' }}>
                <td style={tdStyle}><strong>{fmtDate(row.exam_date)}</strong></td>
                <td style={tdStyle}><span style={{ fontWeight: 600, color: '#1e40af' }}>{row.subject}</span></td>
                <td style={tdStyle}>{row.syllabus || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <button
                    onClick={() => handleDelete(row.schedule_id)}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}
                    title="Remove"
                  >✕</button>
                </td>
              </tr>
            ))}

            {/* Add row inline */}
            {examName.trim() && (
              <tr style={{ background: '#f0fdf4', borderTop: '2px dashed #22c55e' }}>
                <td style={tdStyle}>
                  <input
                    type="date"
                    style={inputStyle}
                    value={draft.exam_date}
                    onChange={(e) => setDraft((p) => ({ ...p, exam_date: e.target.value }))}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    style={inputStyle}
                    placeholder="ENGLISH"
                    value={draft.subject}
                    onChange={(e) => setDraft((p) => ({ ...p, subject: e.target.value }))}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    style={{ ...inputStyle, width: '100%' }}
                    placeholder="Topic / Chapter"
                    value={draft.syllabus}
                    onChange={(e) => setDraft((p) => ({ ...p, syllabus: e.target.value }))}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleAdd}
                    disabled={saving || !draft.exam_date || !draft.subject.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {saving ? '…' : '+ Add'}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle = { padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 13, letterSpacing: '0.04em' };
const tdStyle = { padding: '9px 14px', borderBottom: '1px solid #e5e7eb' };
const inputStyle = { padding: '6px 10px', border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none' };

// ── Main Exams Page ──────────────────────────────────────────────────────────
export default function Exams() {
  const [subTab, setSubTab] = useState('exams'); // 'exams' | 'attendance' | 'homework' | 'planner' | 'examschedule' | 'progresscards'

  return (
    <div className="page">
      <div className="tabs">
        <button className={`tab${subTab === 'exams'         ? ' active' : ''}`} onClick={() => setSubTab('exams')}>📝 Exams &amp; Results</button>
        <button className={`tab${subTab === 'attendance'    ? ' active' : ''}`} onClick={() => setSubTab('attendance')}>📋 Attendance</button>
        <button className={`tab${subTab === 'homework'      ? ' active' : ''}`} onClick={() => setSubTab('homework')}>📚 Homework</button>
        <button className={`tab${subTab === 'examschedule'  ? ' active' : ''}`} onClick={() => setSubTab('examschedule')}>📅 Exam Schedule</button>
        <button className={`tab${subTab === 'planner'       ? ' active' : ''}`} onClick={() => setSubTab('planner')}>🗓️ Timetable</button>
        <button className={`tab${subTab === 'progresscards' ? ' active' : ''}`} onClick={() => setSubTab('progresscards')}>📊 Progress Cards</button>
      </div>
      {subTab === 'exams'
        ? <ExamsPanel />
        : subTab === 'attendance'
          ? <AttendancePanel />
          : subTab === 'homework'
            ? <HomeworkAssignments />
            : subTab === 'examschedule'
              ? <ExamSchedulePanel />
              : subTab === 'planner'
                ? <TimetableEvents />
                : <ProgressCardsPanel />}
    </div>
  );
}

// ── Attendance Panel ──────────────────────────────────────────────────────────
function AttendancePanel() {
  const { year: globalYear } = useYear();

  const selectedYear = globalYear;
  const [classes, setClasses]           = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [locked, setLocked]             = useState(false);
  const [markedDates, setMarkedDates]   = useState([]);
  const [viewMode, setViewMode]         = useState('mark'); // 'mark' | 'summary'
  const [summary, setSummary]           = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryMonth, setSummaryMonth] = useState(''); // '' = all-time, '1'-'12' = specific month
  // in/out time state: { [studentId]: { in_time, out_time, out_reason } }
  const [timings, setTimings]           = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());

  const OUT_REASONS = ['Health Issue', 'Outing', 'Family Function', 'Early Pickup', 'Emergency', 'Other'];

  const updateTiming = (studentId, field, value) => {
    if (locked) return;
    setTimings(prev => ({ ...prev, [studentId]: { ...(prev[studentId] || {}), [field]: value } }));
    setSaved(false);
  };

  const toggleExpand = (studentId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId); else next.add(studentId);
      return next;
    });
  };

  // Fetch classes that have active students for the selected year
  useEffect(() => {
    setSelectedClass('');
    setStudents([]);
    studentsAPI.getAll({ year: selectedYear, status: 'active' }).then((res) => {
      const data = res.data.data || [];
      const uniqueClasses = [...new Set(data.map(s => s.class))].sort();
      setClasses(uniqueClasses);
    }).catch(() => setClasses([]));
  }, [selectedYear]);

  // Load students + attendance when class/date changes
  useEffect(() => {
    if (!selectedClass || !selectedDate || viewMode !== 'mark') return;
    setLoading(true);
    setSaved(false);
    setStudents([]);
    setTimings({});
    setExpandedRows(new Set());
    attendanceAPI.get({ class: selectedClass, date: selectedDate, year: selectedYear })
      .then(res => {
        const list = res.data.data?.students || [];
        setStudents(list);
        // Populate timings from saved data
        const t = {};
        list.forEach(s => {
          if (s.in_time || s.out_time || s.out_reason) {
            t[s.student_id] = { in_time: s.in_time || '', out_time: s.out_time || '', out_reason: s.out_reason || '' };
          }
        });
        setTimings(t);
        setExpandedRows(new Set(Object.keys(t).map(Number)));
        // If any student already has attendance marked for this date, freeze the table
        setLocked(list.some(s => s.status));
      })
      .catch((err) => { console.error('Attendance load error:', err.response?.data || err.message); setStudents([]); })
      .finally(() => setLoading(false));
  }, [selectedClass, selectedDate, selectedYear, viewMode]);

  // Load marked dates for the class
  useEffect(() => {
    if (!selectedClass) return;
    const month = parseInt(selectedDate.split('-')[1], 10);
    attendanceAPI.getDates({ class: selectedClass, year: selectedYear, month })
      .then(res => setMarkedDates(res.data.data || []))
      .catch(() => setMarkedDates([]));
  }, [selectedClass, selectedDate, selectedYear]);

  // Load summary
  useEffect(() => {
    if (viewMode !== 'summary' || !selectedClass) return;
    setSummaryLoading(true);
    const params = { class: selectedClass, year: selectedYear };
    if (summaryMonth) params.month = summaryMonth;
    attendanceAPI.getSummary(params)
      .then(res => setSummary(res.data.data || []))
      .catch(() => setSummary([]))
      .finally(() => setSummaryLoading(false));
  }, [viewMode, selectedClass, selectedYear, summaryMonth]);

  const updateStatus = (studentId, status) => {
    if (locked) return;
    setStudents(prev => prev.map(s => s.student_id === studentId ? { ...s, status } : s));
    setSaved(false);
  };

  const markAllPresent = () => {
    if (locked) return;
    setStudents(prev => prev.map(s => ({ ...s, status: 'present' })));
    setSaved(false);
  };

  const markAllAbsent = () => {
    if (locked) return;
    setStudents(prev => prev.map(s => ({ ...s, status: 'absent' })));
    setSaved(false);
  };

  const handleSave = async () => {
    // Validate in/out times before saving
    for (const s of students) {
      const t = timings[s.student_id] || {};
      if (t.in_time && t.out_time && t.out_time <= t.in_time) {
        alert(`Invalid time for ${s.student_name}: Out time must be later than In time.`);
        return;
      }
    }
    const records = students.filter(s => s.status).map(s => ({
      student_id: s.student_id,
      status:     s.status,
      in_time:    timings[s.student_id]?.in_time    || null,
      out_time:   timings[s.student_id]?.out_time   || null,
      out_reason: timings[s.student_id]?.out_reason || null,
    }));
    if (!records.length) { alert('Please mark attendance for at least one student'); return; }
    setSaving(true);
    try {
      await attendanceAPI.save({ class: selectedClass, date: selectedDate, year: selectedYear, records });
      setSaved(true);
      setLocked(true);
      // Refresh marked dates
      const month = parseInt(selectedDate.split('-')[1], 10);
      attendanceAPI.getDates({ class: selectedClass, year: selectedYear, month })
        .then(res => setMarkedDates(res.data.data || [])).catch(() => {});
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    }
    setSaving(false);
  };

  const statusColors = { present: '#16a34a', absent: '#ef4444', half_day: '#f59e0b' };
  const statusLabels = { present: '✅ Present', absent: '❌ Absent', half_day: '🕐 Half Day' };
  const isMarked = markedDates.includes(selectedDate);

  const presentCount = students.filter(s => s.status === 'present').length;
  const absentCount  = students.filter(s => s.status === 'absent').length;
  const halfCount    = students.filter(s => s.status === 'half_day').length;
  const unmarked     = students.filter(s => !s.status).length;

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>📋 Attendance</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn ${viewMode === 'mark' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('mark')}
          >Mark Attendance</button>
          <button
            className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('summary')}
          >View Summary</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ minWidth: 120, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontWeight: 600, background: '#f9fafb', color: '#374151' }}>
          {selectedYear}
        </span>
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} style={{ minWidth: 140, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}>
          <option value="">Select Class</option>
          {classes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {viewMode === 'mark' && (
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            lang="en-GB"
            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        )}
        {viewMode === 'mark' && isMarked && (
          <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
            ✅ Attendance marked for this date
          </span>
        )}
      </div>

      {!selectedClass ? (
        <div className="empty-state"><p>Select a class to continue.</p></div>
      ) : viewMode === 'mark' ? (
        <>
          {/* Summary bar */}
          {students.length > 0 && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ background: '#f0fdf4', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Total: {students.length}
              </span>
              <span style={{ background: '#dcfce7', color: '#16a34a', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Present: {presentCount}
              </span>
              <span style={{ background: '#fee2e2', color: '#ef4444', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Absent: {absentCount}
              </span>
              <span style={{ background: '#fef3c7', color: '#d97706', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Half Day: {halfCount}
              </span>
              {unmarked > 0 && (
                <span style={{ background: '#f1f5f9', color: '#64748b', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                  Unmarked: {unmarked}
                </span>
              )}
            </div>
          )}

          {loading ? (
            <div className="loading">Loading students...</div>
          ) : students.length === 0 ? (
            <div className="empty-state"><p>No active students found for this class.</p></div>
          ) : (
            <>
              <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-success btn-sm" onClick={markAllPresent} disabled={locked} style={locked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>✅ Mark All Present</button>
                <button className="btn btn-sm" onClick={markAllAbsent} disabled={locked}
                  style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 12px', fontWeight: 600, cursor: locked ? 'not-allowed' : 'pointer', fontSize: 13, opacity: locked ? 0.5 : 1 }}>
                  ❌ Mark All Absent
                </button>
                {locked && (
                  <>
                    <span style={{ fontSize: 12, color: '#64748b' }}>🔒 Attendance is saved and locked</span>
                    <button className="btn btn-sm btn-secondary" onClick={() => setLocked(false)} title="Edit Attendance">✏️ Edit Attendance</button>
                  </>
                )}
              </div>
              <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Student Name</th>
                    <th>Father's Name</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center', width: 80 }}>In/Out</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, idx) => {
                    const t = timings[s.student_id] || {};
                    const hasTime = t.in_time || t.out_time || t.out_reason;
                    const isExpanded = expandedRows.has(s.student_id);
                    return (
                      <React.Fragment key={s.student_id}>
                        <tr style={{ background: s.status === 'absent' ? '#fff5f5' : s.status === 'half_day' ? '#fffbeb' : '' }}>
                          <td>{idx + 1}</td>
                          <td style={{ fontWeight: 500 }}>{s.student_name}</td>
                          <td>{s.father_name || '—'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              {['present', 'absent', 'half_day'].map(st => (
                                <button
                                  key={st}
                                  onClick={() => updateStatus(s.student_id, st)}
                                  disabled={locked}
                                  style={{
                                    padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: locked ? 'not-allowed' : 'pointer',
                                    border: s.status === st ? `2px solid ${statusColors[st]}` : '1px solid #d1d5db',
                                    background: s.status === st ? (st === 'present' ? '#dcfce7' : st === 'absent' ? '#fee2e2' : '#fef3c7') : '#fff',
                                    color: s.status === st ? statusColors[st] : '#64748b',
                                    opacity: locked ? 0.7 : 1
                                  }}
                                >
                                  {statusLabels[st]}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => toggleExpand(s.student_id)}
                              title="Record In/Out Time"
                              style={{
                                padding: '4px 8px', fontSize: 13, borderRadius: 6, cursor: 'pointer', border: 'none',
                                background: hasTime ? '#dbeafe' : '#f1f5f9',
                                color: hasTime ? '#1d4ed8' : '#64748b', fontWeight: hasTime ? 700 : 400
                              }}
                            >
                              ⏱{hasTime ? ' ✓' : ''}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: '#f8faff' }}>
                            <td colSpan={5} style={{ padding: '10px 16px 14px' }}>
                              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>IN TIME</label>
                                  <input
                                    type="time"
                                    value={t.in_time || ''}
                                    disabled={locked}
                                    onChange={e => updateTiming(s.student_id, 'in_time', e.target.value)}
                                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 120 }}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>OUT TIME</label>
                                  <input
                                    type="time"
                                    value={t.out_time || ''}
                                    disabled={locked}
                                    onChange={e => {
                                      const outVal = e.target.value;
                                      if (t.in_time && outVal && outVal <= t.in_time) {
                                        alert('Out time must be later than In time.');
                                        return;
                                      }
                                      updateTiming(s.student_id, 'out_time', outVal);
                                    }}
                                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 120 }}
                                  />
                                  {t.in_time && t.out_time && t.out_time <= t.in_time && (
                                    <div style={{ color: '#dc2626', fontSize: 11, marginTop: 2 }}>Must be after in time</div>
                                  )}
                                </div>
                                <div style={{ flex: '1 1 220px' }}>
                                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>REASON FOR GOING OUT</label>
                                  <select
                                    value={OUT_REASONS.includes(t.out_reason) ? t.out_reason : (t.out_reason ? 'Other' : '')}
                                    disabled={locked}
                                    onChange={e => updateTiming(s.student_id, 'out_reason', e.target.value)}
                                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 180 }}
                                  >
                                    <option value="">— Select reason —</option>
                                    {OUT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                  </select>
                                </div>
                                {(t.out_reason === 'Other' || (t.out_reason && !OUT_REASONS.includes(t.out_reason))) && (
                                  <div style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>SPECIFY</label>
                                    <input
                                      type="text"
                                      placeholder="Describe reason..."
                                      value={OUT_REASONS.includes(t.out_reason) ? '' : (t.out_reason || '')}
                                      disabled={locked}
                                      onChange={e => updateTiming(s.student_id, 'out_reason', e.target.value)}
                                      style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: '100%' }}
                                    />
                                  </div>
                                )}
                                {hasTime && !locked && (
                                  <button
                                    onClick={() => { setTimings(prev => { const n = { ...prev }; delete n[s.student_id]; return n; }); setSaved(false); }}
                                    style={{ padding: '5px 10px', fontSize: 12, border: '1px solid #fca5a5', borderRadius: 6, background: '#fff5f5', color: '#dc2626', cursor: 'pointer' }}
                                  >✕ Clear</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                {saved && <span style={{ color: '#16a34a', fontWeight: 600, alignSelf: 'center' }}>✅ Saved successfully!</span>}
                {!locked && (
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : '💾 Save Attendance'}
                  </button>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        /* Summary View */
        <>
          {/* Month filter for summary */}
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Filter by Month:</label>
            <select
              value={summaryMonth}
              onChange={e => setSummaryMonth(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            >
              <option value="">All Months (Full Year)</option>
              {['January','February','March','April','May','June','July','August','September','October','November','December']
                .map((m, i) => <option key={i+1} value={String(i+1)}>{m}</option>)}
            </select>
            {summaryMonth && (
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Showing data for {['January','February','March','April','May','June','July','August','September','October','November','December'][parseInt(summaryMonth)-1]} only
              </span>
            )}
          </div>
          {summaryLoading ? (
            <div className="loading">Loading summary...</div>
          ) : summary.length === 0 ? (
            <div className="empty-state"><p>No attendance data found for this class.</p></div>
          ) : (
            <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Student Name</th>
                  <th style={{ textAlign: 'center' }}>Total Days</th>
                  <th style={{ textAlign: 'center' }}>Present</th>
                  <th style={{ textAlign: 'center' }}>Absent</th>
                  <th style={{ textAlign: 'center' }}>Half Day</th>
                  <th style={{ textAlign: 'center' }}>Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((s, idx) => {
                  const total  = s.total_days || 0;
                  const pct    = total > 0 ? (((s.present_days + s.half_days * 0.5) / total) * 100).toFixed(1) : '—';
                  const pctNum = parseFloat(pct) || 0;
                  return (
                    <tr key={s.student_id}>
                      <td>{idx + 1}</td>
                      <td style={{ fontWeight: 500 }}>{s.student_name}</td>
                      <td style={{ textAlign: 'center' }}>{total}</td>
                      <td style={{ textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>{s.present_days}</td>
                      <td style={{ textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>{s.absent_days}</td>
                      <td style={{ textAlign: 'center', color: '#d97706', fontWeight: 600 }}>{s.half_days}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: pctNum >= 75 ? '#16a34a' : pctNum >= 50 ? '#f59e0b' : '#ef4444' }}>
                        {pct}{pct !== '—' ? '%' : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ExamsPanel() {
  const { year } = useYear();
  const [exams,       setExams]       = useState([]);
  const [classes,     setClasses]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showCreate,  setShowCreate]  = useState(false);
  const [activeExam,  setActiveExam]  = useState(null);
  const [editExam,      setEditExam]      = useState(null);
  const [analyticsExam, setAnalyticsExam] = useState(null); // { exam_id, exam_name }
  const [filterClass,   setFilterClass]   = useState('');
  const [filterType,    setFilterType]    = useState('');

  const loadExams = useCallback(() => {
    setLoading(true);
    examsAPI.getAll({ year, class: filterClass || undefined, exam_type: filterType || undefined })
      .then((res) => setExams(res.data.data || []))
      .catch(() => setExams([]))
      .finally(() => setLoading(false));
  }, [year, filterClass, filterType]);

  useEffect(() => { loadExams(); }, [loadExams]);
  useEffect(() => {
    if (!year) return;
    studentsAPI.getClasses(year).then((res) => setClasses(res.data.data || [])).catch(() => {});
  }, [year]);

  const handleDelete = async (examId, examName) => {
    if (!window.confirm(`Delete "${examName}" and all its results?`)) return;
    await examsAPI.delete(examId);
    loadExams();
  };

  const handleEdit = async (examId) => {
    try {
      const res = await examsAPI.getById(examId);
      setEditExam(res.data.data);
    } catch {
      alert('Failed to load exam details');
    }
  };

  const examTypeLabel = { weekly: 'Weekly', monthly: 'Monthly', annual: 'Annual' };
  const examTypeBadge = { weekly: '#3b82f6', monthly: '#8b5cf6', annual: '#ef4444' };

  if (activeExam) {
    return <div className="page-content"><MarkEntry examId={activeExam} onBack={() => { setActiveExam(null); loadExams(); }} /></div>;
  }

  // ── Exam analytics ──
  const totalExams   = exams.length;
  const weeklyCount  = exams.filter((e) => e.exam_type === 'weekly').length;
  const monthlyCount = exams.filter((e) => e.exam_type === 'monthly').length;
  const annualCount  = exams.filter((e) => e.exam_type === 'annual').length;
  const uniqueClasses = [...new Set(exams.map((e) => e.class))].length;

  return (
    <div>
      {/* ── Exam KPIs ── */}
      {!loading && exams.length > 0 && (
        <div className="exam-analytics-row" style={{ marginTop: '.5rem' }}>
          {[
            { label: 'Total Exams',   value: totalExams,    cls: '' },
            { label: 'Weekly',        value: weeklyCount,   cls: '' },
            { label: 'Monthly',       value: monthlyCount,  cls: '' },
            { label: 'Annual',        value: annualCount,   cls: 'fail' },
            { label: 'Classes',       value: uniqueClasses, cls: 'grade' },
          ].map((k) => (
            <div key={k.label} className={`exam-kpi ${k.cls}`}>
              <div className="exam-kpi-value">{k.value}</div>
              <div className="exam-kpi-label">{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="page-header">
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--slate-900)' }}>📝 Exam List</h2>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Create Exam</button>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="filter-select">
          <option value="">All Classes</option>
          {classes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
          <option value="">All Types</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="annual">Annual</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading exams…</div>
      ) : exams.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <div className="empty-state-title">No exams yet</div>
            <div className="empty-state-sub">Create your first exam to start tracking student performance.</div>
            <div className="empty-state-action"><button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Create Exam</button></div>
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Exam Name</th>
                <th>Type</th>
                <th>Class</th>
                <th>Date</th>
                <th>Subjects</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exams.map((e) => (
                <tr key={e.exam_id}>
                  <td style={{ fontWeight: 600, color: 'var(--slate-900)' }}>{e.exam_name}</td>
                  <td>
                    <span className="badge" style={{
                      background: e.exam_type === 'weekly' ? '#dbeafe' : e.exam_type === 'monthly' ? '#ede9fe' : '#fee2e2',
                      color:      e.exam_type === 'weekly' ? '#1d4ed8' : e.exam_type === 'monthly' ? '#7c3aed' : '#b91c1c',
                      borderColor:e.exam_type === 'weekly' ? '#bfdbfe' : e.exam_type === 'monthly' ? '#c4b5fd' : '#fecaca',
                    }}>
                      {examTypeLabel[e.exam_type] || e.exam_type}
                    </span>
                  </td>
                  <td><span className="class-badge">{e.class}</span></td>
                  <td style={{ color: 'var(--slate-600)' }}>{e.exam_date ? new Date(e.exam_date).toLocaleDateString('en-IN') : '—'}</td>
                  <td>
                    {e.subjects_list
                      ? e.subjects_list.split(', ').slice(0, 3).map((s, i) => (
                          <span key={i} style={{ display: 'inline-block', background: 'var(--slate-100)', border: '1px solid var(--slate-200)', borderRadius: 4, padding: '1px 6px', fontSize: 11, marginRight: 3, marginBottom: 2 }}>{s}</span>
                        ))
                      : <span style={{ color: 'var(--slate-400)', fontSize: '.82rem' }}>{e.subject_count} subjects</span>
                    }
                    {e.subjects_list && e.subjects_list.split(', ').length > 3 && (
                      <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>+{e.subjects_list.split(', ').length - 3}</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-sm btn-primary" onClick={() => setActiveExam(e.exam_id)}>✏️ Enter Marks</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(e.exam_id)} title="Edit exam details">⚙️ Edit</button>
                      <button className="btn btn-sm" onClick={() => setAnalyticsExam({ exam_id: e.exam_id, exam_name: e.exam_name })}
                        style={{ background: '#f3e8ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
                        title="View analytics">📊</button>
                      <button className="btn btn-sm btn-danger"  onClick={() => handleDelete(e.exam_id, e.exam_name)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <ExamSetupForm
          classes={classes}
          year={year}
          onSaved={() => { setShowCreate(false); loadExams(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}
      {editExam && (
        <ExamEditForm
          exam={editExam}
          onSaved={() => { setEditExam(null); loadExams(); }}
          onCancel={() => setEditExam(null)}
        />
      )}
      {analyticsExam && (
        <ExamAnalyticsModal
          examId={analyticsExam.exam_id}
          examName={analyticsExam.exam_name}
          onClose={() => setAnalyticsExam(null)}
        />
      )}
    </div>
  );
}

// ── Progress Cards Panel ──────────────────────────────────────────────────────
// Class-wise WhatsApp broadcast. One school link — parents enter mobile number.
function ProgressCardsPanel() {
  const { year } = useYear();
  const sc = sessionStorage.getItem('gs_school_code');
  const schoolName = sessionStorage.getItem('gs_school_name') || '';

  const [classes, setClasses]           = useState([]);
  const [classCounts, setClassCounts]   = useState({});
  const [loading, setLoading]           = useState(true);
  const [sentClasses, setSentClasses]   = useState([]);
  const [copiedClass, setCopiedClass]   = useState(null);

  const schoolLink = `${window.location.origin}/progress-card/${encodeURIComponent(sc)}`;

  useEffect(() => {
    setLoading(true);
    studentsAPI.getAll({ year, status: 'active' }).then(res => {
      const data = res.data.data || [];
      const groups = {};
      data.forEach(s => {
        const cls = s.class || 'Unknown';
        groups[cls] = (groups[cls] || 0) + 1;
      });
      setClasses(Object.keys(groups).sort());
      setClassCounts(groups);
    }).catch(() => { setClasses([]); setClassCounts({}); })
      .finally(() => setLoading(false));
  }, [year]);

  const buildMessage = (cls) =>
    `🏫 *${schoolName || 'School'}* నుండి నమస్కారం 🙏\n\n` +
    `*Class ${cls}* తల్లిదండ్రులకు,\n\n` +
    `మీ పిల్లల *Progress Card* చూడటానికి ఈ లింక్ క్లిక్ చేయండి:\n` +
    `👉 ${schoolLink}\n\n` +
    `📱 మీ రిజిస్టర్డ్ మొబైల్ నంబర్ ఎంటర్ చేస్తే మీ పిల్లల Progress Card వస్తుంది.\n\n` +
    `(Attendance, Exam Marks, Fee Details అన్నీ ఉంటాయి)\n\n` +
    `ధన్యవాదాలు,\n*${schoolName || 'School'}*`;

  const handleWhatsApp = (cls) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(buildMessage(cls))}`, '_blank');
    setSentClasses(prev => prev.includes(cls) ? prev : [...prev, cls]);
  };

  const handleCopy = async (cls) => {
    try {
      await navigator.clipboard.writeText(buildMessage(cls));
      setCopiedClass(cls);
      setTimeout(() => setCopiedClass(null), 2000);
    } catch { alert('Copy failed'); }
  };

  const totalStudents = Object.values(classCounts).reduce((a, b) => a + b, 0);
  const allSent = classes.length > 0 && sentClasses.length >= classes.length;

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>📊 Progress Cards</h2>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {totalStudents} students in {classes.length} classes • {sentClasses.length}/{classes.length} sent
        </span>
      </div>

      {/* How it works */}
      <div style={{ background: '#eff6ff', border: '2px solid #3b82f6', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 6, fontSize: 14 }}>✨ How it works</div>
        <div style={{ fontSize: '.85rem', color: '#1e3a5f', lineHeight: 1.8 }}>
          1️⃣ Click <strong>"📲 WhatsApp"</strong> for each class<br/>
          2️⃣ Select that class's parent WhatsApp group → Send<br/>
          3️⃣ Parents open the link → enter mobile number → see their child's Progress Card<br/>
          <span style={{ color: '#64748b' }}>(Attendance, Exam Marks, Fee Details అన్నీ ఉంటాయి)</span>
        </div>
      </div>

      {/* School Link */}
      <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>📎 Link:</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1e40af', wordBreak: 'break-all' }}>{schoolLink}</span>
      </div>

      {loading ? (
        <div className="loading">Loading classes...</div>
      ) : classes.length === 0 ? (
        <div className="empty-state"><p>No active students found for {year}.</p></div>
      ) : (
        <>
          {/* Class list */}
          {classes.map(cls => {
            const count = classCounts[cls] || 0;
            const isSent = sentClasses.includes(cls);
            const isCopied = copiedClass === cls;
            return (
              <div key={cls} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '14px 16px', marginBottom: 8, borderRadius: 10,
                border: `2px solid ${isSent ? '#86efac' : '#e2e8f0'}`,
                background: isSent ? '#f0fdf4' : '#fff'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#1e3a5f' }}>
                    {isSent && '✅ '}Class {cls}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{count} student{count !== 1 ? 's' : ''}</div>
                </div>
                <button
                  onClick={() => handleWhatsApp(cls)}
                  style={{
                    padding: '10px 18px', borderRadius: 8, border: 'none',
                    background: isSent ? '#86efac' : '#25D366', color: '#fff',
                    cursor: 'pointer', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap'
                  }}
                >
                  {isSent ? '✅ Sent' : '📲 WhatsApp'}
                </button>
              </div>
            );
          })}

          {allSent && (
            <div style={{ textAlign: 'center', marginTop: 20, fontSize: 18, fontWeight: 700, color: '#16a34a' }}>
              ✅ All {classes.length} classes done!
            </div>
          )}
        </>
      )}
    </div>
  );
}
